import type { FC } from 'react';

import type { CarePlanViewModel } from '@eluve/api-contract';
import { Icon, Stack, Text, cn } from '@eluve/components';

export const PhaseSection: FC<{
  phase: CarePlanViewModel['phase'];
  startDate: string | null;
  disabled?: boolean;
  onSelectPhase: (phase: number) => void;
}> = ({ phase, startDate, disabled, onSelectPhase }) => (
  <section className="w-full rounded-xl border border-border-secondary bg-bg-primary p-4 shadow-sm">
    <Stack direction="horizontal" align="baseline" gap={3}>
      <Text weight="semibold">Phase</Text>
      {startDate && (
        <Text size="xs" color="secondary">
          Plan started {startDate}
        </Text>
      )}
    </Stack>
    <Stack
      direction="horizontal"
      align="start"
      justify="between"
      gap={2}
      className="mt-4"
    >
      {Array.from({ length: phase.total }, (_, i) => {
        const number = i + 1;
        const isCurrent = number === phase.current;
        const isDone = number < phase.current;
        return (
          <button
            key={number}
            type="button"
            disabled={disabled}
            onClick={() => onSelectPhase(number)}
            className="group flex min-w-0 flex-1 flex-col items-center gap-1.5"
            aria-label={`Set phase ${number}`}
            aria-current={isCurrent ? 'step' : undefined}
          >
            <span
              className={cn(
                'flex size-8 items-center justify-center rounded-full border text-sm transition-colors',
                isCurrent &&
                  'border-border-insights-subtle bg-bg-insights-strong-primary text-text-insights-strong',
                isDone &&
                  'border-border-secondary bg-bg-secondary text-text-secondary',
                !isCurrent &&
                  !isDone &&
                  'border-border-secondary text-text-secondary group-hover:border-border-insights-subtle',
              )}
            >
              {isDone ? <Icon name="Check" size="xs" /> : number}
            </span>
            <Text
              size="xs"
              color={isCurrent ? 'primary' : 'secondary'}
              className={cn('text-center', isCurrent && 'font-medium')}
            >
              {phase.names[i] ?? `Phase ${number}`}
            </Text>
          </button>
        );
      })}
    </Stack>
  </section>
);
